<template>
    <div class="lg:hidden sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-xl border-b border-white/[0.08]">
        <div class="px-4 pt-3 pb-2.5 space-y-2.5">
            <!-- Search Bar at Top -->
            <div class="relative mb-2 group">
                <Search class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 group-focus-within:text-[#0052cc] transition-colors duration-200" />

                <input
                    type="text"
                    :value="searchQuery"
                    @input="$emit('searchInput', $event)"
                    @focus="$emit('searchFocus')"
                    class="w-full h-11 bg-white/[0.08] border-2 border-white/35 text-white text-[15px] font-medium rounded-xl pl-10 pr-10 focus:outline-none focus:bg-white/[0.12] focus:border-white/65 focus:shadow-[0_0_0_3px_rgba(255,255,255,0.07)] transition-all duration-200"
                />
                <Transition name="placeholder-fade" mode="out-in">
                    <span v-if="!searchQuery"
                          :key="currentPlaceholder"
                          class="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-white/50 text-[15px] font-medium select-none"
                    >
                        {{ currentPlaceholder }}
                    </span>
                </Transition>
                <button
                    v-if="searchQuery"
                    @click="$emit('clearSearch')"
                    class="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-all duration-150"
                    aria-label="Clear search"
                >
                    <X class="w-3 h-3 text-white/80" />
                </button>
            </div>

            <!-- Scrollable tab strip -->
            <div class="relative">
                <!-- Right fade gradient — hints that more tabs exist off-screen -->
                <div class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-zinc-950/95 to-transparent pointer-events-none z-10 rounded-r-[14px]"></div>

                <div class="flex gap-1 p-1 bg-white/[0.04] rounded-[14px] border border-white/[0.06] overflow-x-auto tabs-scroll">
                    <button
                        v-for="tab in tabs"
                        :key="tab.value"
                        @click="$emit('updateCurrentView', tab.value)"
                        :class="[
                            'flex-shrink-0 h-9 px-4 rounded-[11px] text-xs font-semibold transition-all',
                            tab.count !== null ? 'flex items-center gap-1.5' : '',
                            currentView === tab.value
                                ? 'bg-white/[0.12] text-white shadow-sm scale-[0.98]'
                                : 'text-white/60 active:scale-95'
                        ]"
                    >
                        {{ tab.label }}
                        <span
                            v-if="tab.count > 0"
                            :class="['min-w-[18px] px-1.5 h-[18px] rounded-full text-white text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0', tab.badgeClass]"
                        >
                            {{ tab.count }}
                        </span>
                    </button>
                </div>
            </div>

            <!-- Sort dropdown -->
            <div v-if="tabsWithFilters.includes(currentView)">
                <select
                    :value="currentSort"
                    @change="$emit('updateCurrentSort', $event.target.value)"
                    class="w-full h-9 bg-white/[0.04] border py-0 border-white/[0.06] text-white text-[13px] font-medium rounded-[11px] pl-3 pr-8 appearance-none focus:outline-none focus:ring-1 focus:ring-white/20 active:scale-[0.98] transition-all"
                    :style="{
                        backgroundImage: `url('data:image/svg+xml,%3Csvg width=%2710%27 height=%276%27 viewBox=%270 0 10 6%27 fill=%27none%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cpath d=%27M1 1L5 5L9 1%27 stroke=%27%23999%27 stroke-width=%271.5%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27/%3E%3C/svg%3E')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center'
                    }"
                >
                    <option v-for="(label, value) in sortOptions" :value="value" :key="value">{{ label }}</option>
                </select>
            </div>
        </div>
    </div>
</template>

<script setup>
import {Search, X} from "lucide-vue-next";
import {computed, ref, onMounted, onUnmounted} from "vue";

const placeholders = [
    'Search auditions...',
    'Search performers...',
    'Find your favorites...',
    'Search by audition titles...',
]

const currentIndex = ref(0)
const currentPlaceholder = ref(placeholders[0])
let interval = null

onMounted(() => {
    interval = setInterval(() => {
        currentIndex.value = (currentIndex.value + 1) % placeholders.length
        currentPlaceholder.value = placeholders[currentIndex.value]
    }, 2000)
})

onUnmounted(() => {
    clearInterval(interval)
})

const props = defineProps({
    currentView: {
        type: String,
    },
    currentSort: {
        type: String,
    },
    isUserLoggedIn: {
        type: Boolean,
    },
    userSponsoredCount: {
        type: Number,
    },
    releasedCount: {
        type: Number,
    },
    totalCount: {
        type: Number,
    },
    sponsoredCount: {
        type: Number,
        default: 0,
    },
    allCount: {
        type: Number,
        default: 0,
    },
    exploreCount: {
        type: Number,
        default: 0,
    },
    sortOptions: {
        type: Object,
    },
    searchQuery: {
        type: String,
        default: ''
    },
    filteredSponsoredCount: {
        type: Number,
        default: null
    },
    filteredReleasedCount: {
        type: Number,
        default: null
    },
    filteredMineCount: {
        type: Number,
        default: null
    },
    filteredAllCount: {
        type: Number,
        default: null
    },
    filteredExploreCount: {
        type: Number,
        default: null
    },
    castingsCount: {
        type: Number,
        default: 0
    }
})

defineEmits(['updateCurrentView', 'updateCurrentSort', 'searchInput', 'clearSearch', 'searchFocus'])

const displaySponsoredCount = computed(() => {
    return props.searchQuery && props.filteredSponsoredCount !== null
        ? props.filteredSponsoredCount
        : props.sponsoredCount
})

const displayReleasedCount = computed(() => {
    return props.searchQuery && props.filteredReleasedCount !== null
        ? props.filteredReleasedCount
        : props.releasedCount
})

const displayMineCount = computed(() => {
    return props.searchQuery && props.filteredMineCount !== null
        ? props.filteredMineCount
        : props.userSponsoredCount
})

const displayAllCount = computed(() => {
    return props.searchQuery && props.filteredAllCount !== null
        ? props.filteredAllCount
        : props.allCount
})

const displayExploreCount = computed(() => {
    return props.searchQuery && props.filteredExploreCount !== null
        ? props.filteredExploreCount
        : props.exploreCount
})

const tabs = computed(() => [
    { value: 'all', label: 'All', count: displayAllCount.value, badgeClass: 'bg-gray-500' },
    { value: 'explore', label: 'Explore', count: displayExploreCount.value, badgeClass: 'bg-gray-500' },
    { value: 'sponsored', label: 'Sponsored', count: displaySponsoredCount.value, badgeClass: 'bg-blue-600' },
    { value: 'released', label: 'Released', count: displayReleasedCount.value, badgeClass: 'bg-green-600' },
    { value: 'mine', label: 'Mine', count: displayMineCount.value, badgeClass: 'bg-red-600', requiresAuth: true },
    { value: 'castings', label: 'Castings', count: props.castingsCount, badgeClass: 'bg-purple-600' },
].filter(tab => !tab.requiresAuth || props.isUserLoggedIn))

const tabsWithFilters = ['all', 'explore']
</script>

<style scoped>
.placeholder-fade-enter-active,
.placeholder-fade-leave-active {
    transition: opacity 0.4s ease, transform 0.4s ease;
}

.placeholder-fade-enter-from {
    opacity: 0;
    transform: translateY(-50%) translateX(10px);
}

.placeholder-fade-enter-to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
}

.placeholder-fade-leave-from {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
}

.placeholder-fade-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(-10px);
}

/* Hide scrollbar across browsers while keeping scroll functionality */
.tabs-scroll {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
}
.tabs-scroll::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
}
</style>
